import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import cancelOrderReasons from 'common/constants/cancelOrderReasons';
import { fillLaterCancelWarning } from 'common/constants/fillLater';
import MarkdownField from 'common/forms/MarkdownField';
import SelectField from 'common/forms/SelectField';
import { validateMaxLength } from 'common/forms/formValidations';
import useInfoDialog from 'common/hooks/useInfoDialog';
import { keys, map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { cancelBulkLineItems } from 'store/thunks/workQueueThunks';

const CancelBulkLineItemDialog = ({ lineItems, open, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const dispatch = useDispatch();

  const lineItemIds = useMemo(() => lineItems.map(({ lineItemId }) => lineItemId), [lineItems]);

  const fillLaterWarning = useMemo(
    () =>
      fillLaterCancelWarning(
        lineItems.filter(({ isMarkedForFillLater }) => isMarkedForFillLater).length
      ),
    [lineItems]
  );

  const handleCancelBulkLineItems = ({ cancelReason, note }) => {
    showInfoDialog({
      title: 'Confirm Cancel Bulk Line Items',
      message: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body1'>
            Are you sure you want to cancel the selected line item(s)? Once canceled, this action
            cannot be undone.
          </Typography>
          {fillLaterWarning && <Typography variant='body1'>{fillLaterWarning}</Typography>}
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Selected items
            </Typography>
            <List
              dense
              sx={{ maxHeight: 200, overflowY: 'auto', border: 1, borderColor: 'divider' }}
            >
              {lineItems.map(({ lineItemId, orderId, patientName, medicationName }) => (
                <ListItem key={lineItemId} sx={{ py: 0 }}>
                  <ListItemText
                    primary={`${orderId || 'N/A'} • ${patientName || 'Unknown Patient'}`}
                    secondary={medicationName || 'N/A'}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      ),
      callback: () => {
        setIsLoading(true);
        dispatch(cancelBulkLineItems({ lineItemIds, cancelReason, note }))
          .then(() => {
            enqueueSnackbar(`Successfully canceled ${lineItems.length} line item(s)`, {
              variant: 'success',
            });
            setIsLoading(false);
            if (onSuccess) {
              onSuccess();
            }
            onClose();
          })
          .catch(() => {
            enqueueSnackbar('Failed to cancel bulk line items', {
              variant: 'error',
            });
            setIsLoading(false);
          });
      },
      cancelable: true,
      isWarning: true,
      confirmButtonText: 'Yes',
    });
  };

  return (
    <>
      <InfoDialog />

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            onClose(event, reason);
          }
        }}
        aria-describedby='CancelBulkLineItem-Dialog-header'
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle id='CancelBulkLineItem-header'>
          {`Cancel ${lineItems.length} Line Item(s)`}
        </DialogTitle>

        <Form
          onSubmit={handleCancelBulkLineItems}
          render={({ handleSubmit, invalid }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <SelectField
                  id='CancelBulkLineItem-cancelReason-dropdown'
                  label='Reason for canceling line items'
                  name='cancelReason'
                  required
                >
                  {map(keys(cancelOrderReasons), (key) => (
                    <MenuItem value={key} key={key}>
                      {cancelOrderReasons[key]}
                    </MenuItem>
                  ))}
                </SelectField>

                <MarkdownField
                  name='note'
                  id='CancelBulkLineItem-cancelNote-input'
                  validations={[
                    validateMaxLength('The note is too long. Please make it shorter', 65435),
                  ]}
                  label='Cancel Note'
                  required
                />

                {fillLaterWarning && (
                  <Alert
                    severity='warning'
                    sx={{
                      mt: 1,
                      bgcolor: '#fff3e0',
                      color: '#ff9800',
                      '& .MuiAlert-icon': { color: '#ff9800' },
                    }}
                  >
                    {fillLaterWarning}
                  </Alert>
                )}
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={isLoading}
                  variant='contained'
                  type='submit'
                  disabled={invalid || isLoading}
                >
                  Submit
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

CancelBulkLineItemDialog.propTypes = {
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      orderId: PropTypes.string,
      patientName: PropTypes.string,
      medicationName: PropTypes.string,
      isMarkedForFillLater: PropTypes.bool,
    })
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

CancelBulkLineItemDialog.defaultProps = {
  onSuccess: undefined,
};

export default CancelBulkLineItemDialog;
